// src/components/feature/VoiceInputButton.tsx
import React, { useState, useRef } from 'react';
import { 
  IconButton, 
  CircularProgress, 
  Tooltip, 
  Popover, 
  Box, 
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { useVoiceRecognition } from '../../hooks/useVoiceRecognition.ts';
import { supportedLanguages } from '../../utils/languageUtils.ts';

interface VoiceInputButtonProps {
  onTextCaptured: (text: string) => void;
}

const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({ onTextCaptured }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-US'); // Default to English
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  const { startRecording, stopRecording, isProcessing, isRecording: recorderIsRecording, error } = useVoiceRecognition({
    language: selectedLanguage,
    onResult: (text) => {
      if (text.trim()) {
        onTextCaptured(text);
      }
      // Use state from the hook instead of local state
      setIsRecording(false);
    }
  });
  
  // Keep local state in sync with hook's state
  React.useEffect(() => {
    setIsRecording(recorderIsRecording);
  }, [recorderIsRecording]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isRecording && !isProcessing) {
      setAnchorEl(event.currentTarget);
    } else if (isRecording) {
      stopRecording();
      setIsRecording(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (event: SelectChangeEvent) => {
    setSelectedLanguage(event.target.value);
  };

  const handleStartRecording = () => {
    startRecording();
    setIsRecording(true);
    handleClose();
  };

  const open = Boolean(anchorEl);
  
  return (
    <>
      <Tooltip title={isRecording ? "Stop recording" : "Add todo by voice"}>
        <IconButton 
          color={isRecording ? "secondary" : "primary"}
          onClick={handleClick}
          ref={buttonRef}
          aria-label={isRecording ? "Stop recording" : "Record voice input"}
        >
          {isProcessing ? (
            <CircularProgress size={24} />
          ) : (
            isRecording ? <StopIcon /> : <MicIcon />
          )}
        </IconButton>
      </Tooltip>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, width: 250 }}>
          <Typography variant="subtitle1" gutterBottom>
            Voice Input Settings
          </Typography>
          
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="language-select-label">Language</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={selectedLanguage}
              label="Language"
              onChange={handleLanguageChange}
            >
              {supportedLanguages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton 
              color="primary" 
              onClick={handleStartRecording}
              aria-label="Start recording"
            >
              <MicIcon />
            </IconButton>
          </Box>
        </Box>
      </Popover>
      
      {error && (
        <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}
    </>
  );
};

export default VoiceInputButton;